import {
  AI_CONTENT_TYPES,
  getContentTypeCountBounds,
} from "@/lib/ai-content-builder/content-types";

// Content types the practice-pack planner may include, in display order.
// Q&A and glossary tests are left out on purpose: they build from saved Q&As
// and terms, which the learning path's knowledge checks already do on save.
export const PROGRAM_CONTENT_TYPE_IDS = [
  "personas",
  "objection-scenarios",
  "discovery-scenarios",
  "pitch-scenarios",
  "outbound-scenarios",
  "play-scenarios",
  "rubrics",
  "faqs",
  "fbiv",
  "glossary",
  "multiple-choice-tests",
];

// Only one of these should appear in a pack — the planner picks the type that
// best fits the source material.
export const PROGRAM_SCENARIO_TYPE_IDS = PROGRAM_CONTENT_TYPE_IDS.filter(
  (id) => AI_CONTENT_TYPES[id]?.domain === "scenario"
);

// One-line "when to include" guidance per type, written for the planner
// prompt. Kept next to the id list so adding a type forces a decision here.
export const PROGRAM_TYPE_PLAN_HINTS = {
  personas:
    "Who the seller talks to. Include 1-3 when the source reveals anything about the buyer, their role, or their pains.",
  "objection-scenarios":
    "Pushback practice. Best when the source mentions pricing, competitors, switching costs, risk, or skepticism.",
  "discovery-scenarios":
    "Question-asking practice. Best when the source is about understanding customer needs, qualification, or early-stage calls.",
  "pitch-scenarios":
    "Message-delivery practice. Best when the source is product/value-proposition heavy (websites, brochures, pitch decks).",
  "outbound-scenarios":
    "Cold outreach practice. Best when the source is about prospecting, cold calls, or booking first meetings.",
  "play-scenarios":
    "Full roleplay situations. Best for rich, multi-step situations (renewals, escalations, follow-up meetings).",
  rubrics:
    "How attempts get scored. Include exactly one whenever any scenario type is included.",
  faqs: "Common buyer questions with strong answers, when the source supports them.",
  fbiv: "Feature-benefit-impact-value talking points, when the source describes product capabilities.",
  glossary: "Terms the team must use consistently, when the source uses distinctive vocabulary.",
  "multiple-choice-tests":
    "A quiz on the material itself. Include 1 when the source has facts a rep must know (product details, process steps, policies); the knowledge checks over Q&As and terms are made separately.",
};

// A learning path is assembled from the other drafts at save time, never
// generated, so it is a program section with its own shape rather than an
// AI_CONTENT_TYPES entry (which would offer it to every generate/save tool).
// The MCP catalog's salesably_create_learning_path is what the save calls.
export const LEARNING_PATH_DRAFT_TYPE_ID = "learning-paths";

export const LEARNING_PATH_DRAFT_TYPE = Object.freeze({
  id: LEARNING_PATH_DRAFT_TYPE_ID,
  label: "Learning path",
  singularLabel: "learning path",
  fields: [
    { name: "name", label: "Name", type: "text" },
    { name: "description", label: "Description", type: "textarea", rows: 4 },
  ],
  getItemTitle: (item, index = 0) =>
    String(item?.name || "").trim() || `Learning path ${index + 1}`,
});

export const PROGRAM_SECTION_TYPE_IDS = [
  ...PROGRAM_CONTENT_TYPE_IDS,
  LEARNING_PATH_DRAFT_TYPE_ID,
];

// The config a program section renders and normalises with: the content
// type's own, or the learning-path draft's.
export function getProgramSectionType(contentType) {
  if (contentType === LEARNING_PATH_DRAFT_TYPE_ID) {
    return LEARNING_PATH_DRAFT_TYPE;
  }
  return AI_CONTENT_TYPES[contentType] || null;
}

export function isLearningPathDraftType(contentType) {
  return contentType === LEARNING_PATH_DRAFT_TYPE_ID;
}

function pluralize(count, singular, plural) {
  return `${count} ${count === 1 ? singular : plural}`;
}

// What the saved path will hold, from the sections the admin has selected:
// read by the review card so the choice is made with the outcome in view.
// Mirrors buildFocusedLearningPathSections: scenarios become practice
// assignments with the last one scored, Q&As and glossary entries become
// study sets with a knowledge check each.
export function describeLearningPathContents(sections = []) {
  const parts = [];
  const selectedCount = (contentType) => {
    const section = sections.find((item) => item.contentType === contentType);
    return section ? section.selectedIds.size : 0;
  };

  for (const contentType of PROGRAM_SCENARIO_TYPE_IDS) {
    const count = selectedCount(contentType);
    if (count === 0) continue;
    const config = AI_CONTENT_TYPES[contentType];
    parts.push(
      `${pluralize(count, config.singularLabel, config.label.toLowerCase())} as practice assignments${
        count > 1 ? ", the last one scored" : ", scored"
      }`
    );
  }
  const faqCount = selectedCount("faqs");
  if (faqCount > 0) {
    parts.push(`${pluralize(faqCount, "Q&A", "Q&As")} with a knowledge check`);
  }
  const glossaryCount = selectedCount("glossary");
  if (glossaryCount > 0) {
    parts.push(
      `${pluralize(glossaryCount, "glossary entry", "glossary entries")} with a knowledge check`
    );
  }
  const testCount = selectedCount("multiple-choice-tests");
  if (testCount > 0) {
    parts.push(pluralize(testCount, "multiple choice test", "multiple choice tests"));
  }
  if (selectedCount("rubrics") > 0) {
    const mapCount = selectedCount("fbiv");
    parts.unshift(
      `a study guide written from the rubric${
        mapCount > 0 ? ` with ${pluralize(mapCount, "capability map", "capability maps")}` : ""
      }`
    );
  }
  return parts.join(" · ");
}

export function getProgramTypeMaxCount(contentType) {
  if (!AI_CONTENT_TYPES[contentType]) return 1;
  // Same ceiling as the single-type builder's typed count input, so "make me
  // 25 FAQs" resolves identically in Ask-AI and manual modes.
  return getContentTypeCountBounds(contentType).max;
}

export function isProgramScenarioType(contentType) {
  return PROGRAM_SCENARIO_TYPE_IDS.includes(contentType);
}

// Maps each scenario content-type the builder can create to the RolePlay
// `type` its saved scenarios are assigned under. Lets the result screen
// deep-link into the assignment form with the right module — and the specific
// new scenario — preselected. Keys are content-type ids; values are RolePlay
// `type` enum values (see ROLE_PLAY_SCENARIO_FIELDS in lib/role-plays).
const CONTENT_TYPE_ROLE_PLAY_TYPES = {
  "objection-scenarios": "OBJECTIONS",
  "pitch-scenarios": "PITCH",
  "discovery-scenarios": "DISCOVERY",
  "outbound-scenarios": "COLD_CALL",
  "play-scenarios": "PLAY",
  "management-scenarios": "MANAGEMENT",
};

export function getRolePlayTypeForContentType(contentType) {
  return CONTENT_TYPE_ROLE_PLAY_TYPES[contentType] || null;
}

// Stable display/save order: personas first (scenarios reference them
// mentally), then the scenario, rubric, supporting knowledge, and the
// learning path that gathers them last.
export function sortProgramItems(items = []) {
  return [...items].sort(
    (a, b) =>
      PROGRAM_SECTION_TYPE_IDS.indexOf(a.contentType) -
      PROGRAM_SECTION_TYPE_IDS.indexOf(b.contentType)
  );
}

// Clamp a requested draft count to what the type's generator supports.
// Rubrics are always exactly one.
export function clampProgramCount(contentType, count) {
  if (contentType === "rubrics") return 1;
  const maxCount = getProgramTypeMaxCount(contentType);
  return Math.min(Math.max(Math.round(Number(count) || 1), 1), maxCount);
}
